/**
 * Landing content runtime.
 * Controls the mobile floating CTA, Judge.me badge mirroring, product-section
 * visibility state, hero scrolling, and below-fold review slider lifecycle.
 */
(function () {
  const wbLandingScript = document.currentScript;
  const WB_LANDING_SECTION_ID = wbLandingScript ? wbLandingScript.dataset.sectionId : '';
  const wbLandingRoot = document.getElementById('landing-content-' + WB_LANDING_SECTION_ID);
  if (!wbLandingRoot || wbLandingRoot.dataset.wbLandingRuntimeInitialized === '1') return;
  wbLandingRoot.dataset.wbLandingRuntimeInitialized = '1';

  function initLandingReviewFallback() {
    const review = wbLandingRoot.querySelector('[data-landing-review]');
    const appRoot = review ? review.querySelector('[data-landing-review-app]') : null;
    const fallback = review
      ? review.querySelector('[data-landing-review-fallback] .landing-review-fallback__badge')
      : null;
    if (!review || !appRoot || !fallback) return;

    function syncRealReview() {
      const liveBadge = appRoot.querySelector('.jdgm-prev-badge');
      if (!liveBadge || !liveBadge.querySelector('.jdgm-prev-badge__stars')) return false;

      const realRating = parseFloat(liveBadge.getAttribute('data-average-rating'));
      const realCount = parseInt(liveBadge.getAttribute('data-number-of-reviews'), 10);
      if (isNaN(realRating) || realRating <= 0 || isNaN(realCount) || realCount <= 0) return false;

      const ratingText = fallback.querySelector('.landing-review-fallback__rating');
      const reviewLink = fallback.querySelector('.landing-review-fallback__count');
      fallback.setAttribute('data-average-rating', String(realRating));
      fallback.setAttribute('data-number-of-reviews', String(realCount));
      if (ratingText) ratingText.textContent = realRating.toFixed(1);
      if (reviewLink) reviewLink.textContent = realCount + ' Reviews';
      review.dataset.landingReviewLive = '1';
      return true;
    }

    if (syncRealReview()) return;

    let observer = null;
    let timeout = null;

    function cleanup() {
      if (observer) observer.disconnect();
      document.removeEventListener('jdgm.starsRendered', onStarsRendered);
      if (timeout) clearTimeout(timeout);
    }

    function onStarsRendered() {
      if (syncRealReview()) cleanup();
    }

    if ('MutationObserver' in window) {
      observer = new MutationObserver(function () {
        if (syncRealReview()) cleanup();
      });
      observer.observe(appRoot, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-average-rating', 'data-number-of-reviews']
      });
    }

    document.addEventListener('jdgm.starsRendered', onStarsRendered);
    timeout = setTimeout(cleanup, 15000);
  }

  initLandingReviewFallback();

  (function () {
      const MOB_BREAKPOINT = 768;
      const sectionId = 'landing-content-' + WB_LANDING_SECTION_ID;
      const fixedClass = 'red-flags__hero-intro-button-wrap--fixed';
      const hidingClass = 'red-flags__hero-intro-button-wrap--hiding';
      const sentinelClass = 'red-flags__hero-intro-floatbar-sentinel';
      // 20260522 - 移动端在 landing 内容区时隐藏 ClinicianAI AI 按钮的 <html> 标记。
      // CSS 那边用 @media (max-width: 768px) 包裹，确保 PC 永不触发。
      const HIDE_AI_CLASS = 'landing-hide-ai-mobile';
      let starsCloned = false;

      // 统一切换 <html> 上的 AI 隐藏标记，单点维护避免遗漏；非移动端始终是 false（PC 不隐藏）
      function setAiHidden(hide) {
        const html = document.documentElement;
        if (!html) return;
        if (hide && isMobile()) html.classList.add(HIDE_AI_CLASS);
        else html.classList.remove(HIDE_AI_CLASS);
      }

      // 20260522 - 使用 IntersectionObserver + 哨兵元素方案替换原 scrollY/initialBtnBottom 比较。
      // 原方案在不同视口（如 iPhone 12 Pro 100% 模式）、Safari/Chrome 页面缩放、
      // 资源（Judge.me / frontrow-sticker / 图片）异步加载等场景下，
      // initialBtnBottom 在 200ms 时只计算一次容易拿到错误值，导致浮动条不触发。
      // 新方案：在按钮 wrap 前插入永远占位的哨兵元素，通过 IO 监听其是否滚出视口顶部，
      // 天然支持缩放、资源延迟加载和布局变化。
      let sentinelObs = null;
      let sectionObs = null;
      // 20260522 - 监听商详区是否进入视口，承担两个职责：
      //   1) 浮动条："商详可见即隐藏浮动条"，避免与 ClinicianAI 等浮动按钮在底部区域视觉重叠
      //   2) AI 按钮："商详可见即释放 AI 按钮显示"（移动端 landing 区一直隐藏 AI，进商详才显示）
      // 商详来源有两种：sections/main-product.liquid 的 #mainproduct、
      // sections/featured-product.liquid 的 .featured-product 根 div。
      // 任意一个进入视口都视为商详可见。
      const PRODUCT_DETAIL_SELECTOR = '#mainproduct, .featured-product';
      let productDetailObs = null;
      let productDetailStates = null; // Map<Element, boolean>，跟踪每个商详元素的可见状态
      // 浮动条默认隐藏，需要"哨兵滚出顶部 + section 仍在视口 + 商详未进入视口" 才显示。
      // sectionInView 默认 true：landing-content section 必然在页面初始就是可见的，
      // 这样移动端首次进入时 updateAiHidden 能立刻判定为「应该隐藏 AI」，避免 AI 闪一下再隐藏。
      let sentinelPassed = false; // true = 哨兵已滚出视口顶部
      let sectionInView = true;
      let productDetailInView = false;
      // 商详节点可能由主题编辑器或 App 异步插入，仅观察 landing section 的父容器。
      let productDetailRootObs = null;

      // 性能优化相关：
      //   observedWrap    - 当前 IO 关联的 wrap 节点，wrap 不变就跳过重建
      //   starsReadyObs    - 只观察评分容器，等待 Judge.me 异步填充，不轮询整页
      //   hideTimer       - 浮动条隐藏动画的延时句柄，新调用先 clear 旧的，避免堆积
      //   resizeTimer     - resize debounce 句柄
      //   aiReleaseTimer  - AI 按钮"延迟释放"的句柄：商详进入视口时，浮动条要走 300ms 滑出动画，
      //                     AI 按钮需等浮动条完全消失后再出现，避免两者在屏幕底部同框 0.3s 闪烁。
      let observedWrap = null;
      let starsReadyObs = null;
      let hideTimer = null;
      let resizeTimer = null;
      let aiReleaseTimer = null;
      // 20260603 - 记录 wrap 变 fixed 前的占位高度，用于让哨兵补齐 section 高度，防止闪烁
      let sentinelSpacerH = 0;

      // 将同一轮 DOM 变化触发的 UI 同步合并到下一帧，避免同步重复读取/写入布局。
      function scheduleFrame(callback) {
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(callback);
        } else {
          setTimeout(callback, 0);
        }
      }

      function isMobile() {
        return window.innerWidth <= MOB_BREAKPOINT;
      }

      function getActiveWrap() {
        const section = document.getElementById(sectionId);
        if (!section) return null;
        return section.querySelector('.red-flags__hero-intro-button-wrap') || null;
      }

      function getActiveBtn() {
        const wrap = getActiveWrap();
        return wrap ? wrap.querySelector('.red-flags__hero-intro-button') : null;
      }

      // 确保 wrap 之前有一个哨兵元素：1px 占位、永不变 fixed，位置始终准确
      function ensureSentinel() {
        const wrap = getActiveWrap();
        if (!wrap || !wrap.parentNode) return null;
        const prev = wrap.previousElementSibling;
        if (prev && prev.classList && prev.classList.contains(sentinelClass)) {
          return prev;
        }
        const s = document.createElement('div');
        s.className = sentinelClass;
        s.setAttribute('aria-hidden', 'true');
        wrap.parentNode.insertBefore(s, wrap);
        return s;
      }
  
      // 判断 Judge.me badge 是否「完全渲染完毕」：
      //   1) 有 .jdgm-prev-badge__stars
      //   2) 有 .jdgm-review-count-link
      //   3) 有 data-average-rating 且为有效数值
      //   4) 已被本页面装饰脚本追加 "stars |" 和 "| 10M+ members"（data-daad-review-decorated="1"）
      //   5) .jdgm-prev-badge__text 文本必须等于 data-average-rating 的数字（避免 "384 reviews" 这种过渡占位）
      function isBadgeReady(badge) {
        if (!badge) return false;
        const stars = badge.querySelector('.jdgm-prev-badge__stars, .landing-review-fallback__stars');
        if (!stars) return false;
        const link = badge.querySelector('.jdgm-review-count-link');
        if (!link) return false;
        const rating = badge.getAttribute('data-average-rating');
        if (!rating || isNaN(parseFloat(rating)) || parseFloat(rating) <= 0) return false;
        if (badge.dataset.daadReviewDecorated !== '1') return false;
  
        // 校验 __text 是否已被替换为评分数字（仅数字 / 数字+小数）
        const textEl = badge.querySelector('.jdgm-prev-badge__text, .landing-review-fallback__rating');
        if (textEl) {
          const t = (textEl.textContent || '').trim();
          const ratingNum = parseFloat(rating);
          const textNum = parseFloat(t);
          // 必须是纯数字格式（"4.88"），且与 rating 偏差不超过 0.5
          if (!/^\d+(\.\d+)?$/.test(t)) return false;
          if (isNaN(textNum) || Math.abs(textNum - ratingNum) > 0.5) return false;
        }
        return true;
      }
  
      // 监听源 badge：如果 Judge.me 后续又改它，重置 starsCloned 让浮动条重新克隆。
      let sourceBadgeObserver = null;
      function observeSourceBadge(badge) {
        if (!badge || sourceBadgeObserver) return;
        sourceBadgeObserver = new MutationObserver(function () {
          // 源 badge 发生变化时标记需要重新克隆；下一帧直接同步最终内容，不再依赖轮询。
          starsCloned = false;
          const section = document.getElementById(sectionId);
          if (section) {
            section.querySelectorAll('.red-flags__hero-intro-floatbar-stars').forEach(function (h) {
              h.dataset.starsCloned = '';
            });
          }
          scheduleFrame(ensureStarsCloned);
        });
        sourceBadgeObserver.observe(badge, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['data-average-rating', 'data-number-of-reviews'],
        });
      }
  
      function ensureStarsCloned() {
        if (starsCloned) return;
        const section = document.getElementById(sectionId);
        if (!section) return;
        const hero = section.querySelector('.red-flags__hero');
        if (!hero) return;
        const rating = hero.querySelector('.red-flags__hero-intro-rating');
        const badge = rating
          ? rating.querySelector('[data-landing-review-fallback] .landing-review-fallback__badge, [data-landing-review-app] .jdgm-prev-badge')
          : null;
        if (!badge) return;
  
        // 启动对源 badge 的监听（只挂一次），即使现在还没 ready，后续变化也能触发再次克隆
        observeSourceBadge(badge);
  
        // 必须等 badge 完全渲染 + 装饰脚本完成后再克隆，否则会拿到 "384 reviews" 这种过渡态
        if (!isBadgeReady(badge)) return;
  
        const ratingVal = parseFloat(badge.getAttribute('data-average-rating'));
        const countVal = parseInt(badge.getAttribute('data-number-of-reviews'), 10);
  
        const holders = section.querySelectorAll('.red-flags__hero-intro-floatbar-stars');
        holders.forEach(function (holder) {
          const clone = badge.cloneNode(true);
          // 保留 decorated 标记，避免 MutationObserver 再装饰一次造成重复
          clone.setAttribute('data-daad-review-decorated', '1');
  
          // 兜底：强制把文本改写成最终值，防止源节点处于过渡态时混入错误占位
          if (!isNaN(ratingVal)) {
            const cText = clone.querySelector('.jdgm-prev-badge__text, .landing-review-fallback__rating');
            if (cText) {
              cText.textContent = cText.classList.contains('landing-review-fallback__rating')
                ? ratingVal.toFixed(1)
                : ratingVal.toFixed(2);
            }
          }
          if (!isNaN(countVal)) {
            const cLink = clone.querySelector('.jdgm-review-count-link');
            if (cLink) cLink.textContent = countVal + ' Reviews';
          }
  
          holder.innerHTML = '';
          holder.appendChild(clone);
          holder.dataset.starsCloned = '1';
        });
        starsCloned = true;
      }

      function observeStarsRoot() {
        // Judge.me 仅在当前 section 的评分容器内异步渲染；局部观察替代 500ms/15s 轮询。
        if (starsReadyObs || !('MutationObserver' in window)) return;
        const section = document.getElementById(sectionId);
        const rating = section && section.querySelector('.red-flags__hero-intro-rating');
        if (!rating) return;
        starsReadyObs = new MutationObserver(function () {
          if (!starsCloned) ensureStarsCloned();
        });
        starsReadyObs.observe(rating, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['data-average-rating', 'data-number-of-reviews', 'data-daad-review-decorated']
        });
      }
  
      // 20260603 - 哨兵占位补偿：wrap 变 fixed 脱离文档流时，会让 landing-content section
      // 高度塌缩、底边上移，导致 sectionObs / productDetailObs 在临界滚动位置反复触发，
      // 表现为浮动按钮闪烁。这里在脱流时把哨兵高度撑到 wrap 原占位高度，
      // 使 section 总高度保持不变，从根上断掉反馈循环（同时消除按钮出现时下方内容跳动）。
      function setSentinelSpacer(active) {
        const sentinel = ensureSentinel();
        if (!sentinel) return;
        sentinel.style.height = active && sentinelSpacerH > 0 ? sentinelSpacerH + 'px' : '';
      }

      function hideFixedWrapSmooth(wrap) {
        if (!wrap || !wrap.classList.contains(fixedClass)) return;
        wrap.classList.add(hidingClass);
        // 用单一 timer，反复隐藏不会堆积 setTimeout
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        hideTimer = setTimeout(function () {
          wrap.classList.remove(fixedClass);
          wrap.classList.remove(hidingClass);
          // 回流恢复 wrap 占位的同时收起哨兵占位，二者同步避免布局跳动
          setSentinelSpacer(false);
          hideTimer = null;
        }, 300);
      }

      // AI 按钮显示策略（独立于浮动条逻辑）：
      //   - PC 端（>768px）：永远不挂 HIDE_AI_CLASS，AI 按钮按全站默认显示
      //   - 移动端：landing-content section 还在视口内 且 商详区未进入视口 → 隐藏 AI
      //     （进入商详后或滚过整个 landing 后，AI 按钮恢复全站默认状态）
      function updateAiHidden() {
        if (!isMobile()) {
          setAiHidden(false);
          return;
        }
        if (sectionInView && !productDetailInView) {
          setAiHidden(true);
        } else {
          setAiHidden(false);
        }
      }

      // 立即调用 updateAiHidden 并清掉延迟释放计时器，避免延迟操作覆盖即时操作
      function flushAiHiddenNow() {
        if (aiReleaseTimer) { clearTimeout(aiReleaseTimer); aiReleaseTimer = null; }
        updateAiHidden();
      }

      function updateVisibility() {
        const wrap = getActiveWrap();
        if (!wrap) {
          flushAiHiddenNow();
          return;
        }

        if (!isMobile()) {
          wrap.classList.remove(fixedClass);
          wrap.classList.remove(hidingClass);
          setSentinelSpacer(false);
          flushAiHiddenNow();
          return;
        }

        const wasFixed = wrap.classList.contains(fixedClass) && !wrap.classList.contains(hidingClass);

        // 浮动条显示条件：哨兵已滚出视口顶部 + section 仍在视口内 + 商详区未进入视口
        if (sentinelPassed && sectionInView && !productDetailInView) {
          // → 浮动条显示。瞬时显示无过渡，AI 也立即隐藏，两者切换是瞬时的不会闪烁
          ensureStarsCloned();
          // 脱流前记录 wrap 在文档流中的占位高度（仅在尚未 fixed 时测量，避免拿到 fixed 态高度）
          if (!wrap.classList.contains(fixedClass)) {
            sentinelSpacerH = wrap.offsetHeight;
          }
          wrap.classList.remove(hidingClass);
          wrap.classList.add(fixedClass);
          setSentinelSpacer(true);
          // 关键：取消任何在排队的"隐藏动画收尾"，否则用户在隐藏动画中途反向滚回 landing 时，
          // 排队的 setTimeout 会在 300ms 后把刚 add 回来的 fixedClass 又移除，造成闪烁
          if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
          flushAiHiddenNow();
        } else {
          // → 浮动条要隐藏。如果它原本是 fixed 显示状态，会进入 300ms 滑出动画
          hideFixedWrapSmooth(wrap);

          // 关键防闪烁：浮动条原本可见 + 触发原因是商详进入视口 → 延迟 AI 释放到动画结束后
          // 避免「AI 已经冒出来 + 浮动条还在底部慢慢滑出」的 0.3s 同框
          // 其它路径（哨兵未滚过、section 滚出视口等）AI 状态可立即同步
          if (wasFixed && productDetailInView) {
            if (aiReleaseTimer) clearTimeout(aiReleaseTimer);
            aiReleaseTimer = setTimeout(function () {
              aiReleaseTimer = null;
              updateAiHidden();
            }, 320); // 300ms 浮动条动画 + 20ms 视觉缓冲
          } else {
            flushAiHiddenNow();
          }
        }
      }

      // 用单一 IO 同时监听一组商详元素，任意一个 isIntersecting 即视为商详在视口内。
      // rootMargin '0px 0px -25% 0px'：在元素距离视口底部还有 25% 屏高时就触发 isIntersecting=true，
      // 因为商详根容器很大、起始 top 远在视口下方，严格相交会让浮动条/AI 切换太晚出现视觉滞后。
      function setupProductDetailObserver() {
        if (productDetailObs) return true;
        const els = document.querySelectorAll(PRODUCT_DETAIL_SELECTOR);
        if (!els.length) return false;

        productDetailStates = new Map();
        productDetailObs = new IntersectionObserver(function (entries) {
          for (let i = 0; i < entries.length; i++) {
            productDetailStates.set(entries[i].target, entries[i].isIntersecting);
          }
          let anyInView = false;
          productDetailStates.forEach(function (v) { if (v) anyInView = true; });
          productDetailInView = anyInView;
          updateVisibility();
        }, { threshold: [0], rootMargin: '0px 0px -25% 0px' });

        els.forEach(function (el) {
          productDetailStates.set(el, false);
          productDetailObs.observe(el);
        });
        return true;
      }

      // 商详节点可能异步注入：先即时尝试一次，找不到时只观察当前 section 的父容器。
      function ensureProductDetailObserver() {
        if (productDetailObs) return;
        if (setupProductDetailObserver()) return;
        if (productDetailRootObs || !('MutationObserver' in window)) return;
        const section = document.getElementById(sectionId);
        const parent = section && section.parentNode;
        if (!parent) return;
        productDetailRootObs = new MutationObserver(function () {
          if (setupProductDetailObserver()) {
            productDetailRootObs.disconnect();
            productDetailRootObs = null;
            updateVisibility();
          }
        });
        productDetailRootObs.observe(parent, { childList: true });
      }

      function disconnectObservers() {
        // 响应式切换或 section 重建时集中释放观察器，避免旧节点保留回调。
        if (sentinelObs) { try { sentinelObs.disconnect(); } catch (e) {} sentinelObs = null; }
        if (sectionObs) { try { sectionObs.disconnect(); } catch (e) {} sectionObs = null; }
        if (productDetailObs) { try { productDetailObs.disconnect(); } catch (e) {} productDetailObs = null; }
        if (productDetailRootObs) { try { productDetailRootObs.disconnect(); } catch (e) {} productDetailRootObs = null; }
        if (starsReadyObs) { try { starsReadyObs.disconnect(); } catch (e) {} starsReadyObs = null; }
        if (aiReleaseTimer) { clearTimeout(aiReleaseTimer); aiReleaseTimer = null; }
        productDetailStates = null;
        productDetailInView = false;
        observedWrap = null;
        // 重置时一并取消 AI 隐藏标记，避免组件销毁/重建时 AI 按钮被永久隐藏
        setAiHidden(false);
      }

      function setupObservers() {
        if (!('IntersectionObserver' in window)) return false;

        const section = document.getElementById(sectionId);
        if (!section) return false;

        const wrap = getActiveWrap();
        // 幂等：如果当前 wrap 没变且 Observer 已挂载，无需重建
        if (wrap && wrap === observedWrap && sentinelObs && sectionObs) {
          return true;
        }

        const sentinel = ensureSentinel();
        if (!sentinel) return false;

        disconnectObservers();
        observedWrap = wrap;

        // 哨兵观察：用 IO 判断哨兵当前与视口的位置关系
        //   - isIntersecting=true → 哨兵在视口内 → 还没滚过
        //   - isIntersecting=false 且 boundingClientRect.top < 0 → 哨兵已滚到视口上方 → 触发浮动条
        //   - isIntersecting=false 且 boundingClientRect.top >= 0 → 哨兵还在视口下方 → 还没到
        sentinelObs = new IntersectionObserver(function (entries) {
          for (let i = 0; i < entries.length; i++) {
            const e = entries[i];
            if (e.isIntersecting) {
              sentinelPassed = false;
            } else {
              sentinelPassed = e.boundingClientRect.top < 0;
            }
          }
          updateVisibility();
        }, { threshold: [0] });
        sentinelObs.observe(sentinel);

        // section 观察：section 完全离开视口时强制隐藏浮动条
        sectionObs = new IntersectionObserver(function (entries) {
          for (let i = 0; i < entries.length; i++) {
            sectionInView = entries[i].isIntersecting;
          }
          updateVisibility();
        }, { threshold: [0] });
        sectionObs.observe(section);

        // 商详区（#mainproduct / .featured-product）观察：进入视口即隐藏浮动条 + 释放 AI 按钮
        ensureProductDetailObserver();

        return true;
      }

      function initMobFixBtn() {
        const section = document.getElementById(sectionId);
        const wrap = getActiveWrap();
        const btn = getActiveBtn();
        if (!section || !wrap || !btn) return;

        if (!isMobile()) {
          wrap.classList.remove(fixedClass);
          wrap.classList.remove(hidingClass);
          setSentinelSpacer(false);
          disconnectObservers();
          // 切到桌面也立即确保 AI 按钮恢复（disconnectObservers 内已调用 setAiHidden(false)）
          return;
        }

        // 移动端初始化时立即假定「在 landing 内容区」隐藏 AI，避免 AI 按钮先显示一下再被 IO 触发隐藏
        setAiHidden(true);

        setupObservers();

        // Judge.me 异步渲染由 section 级观察器触发，不再启动 15 秒轮询。
        ensureStarsCloned();
        observeStarsRoot();

        if (!btn.__floatbarClickBound) {
          btn.__floatbarClickBound = true;
          // 20260602 改动：滚动到商详主图的逻辑统一交由下方 landing-hero-scroll 脚本处理
          // （目标为落地页 featured-product 容器 #featureproduct），这里仅负责浮动条收起动画
          btn.addEventListener(
            'click',
            function () {
              if (!isMobile()) return;
              hideFixedWrapSmooth(wrap);
            },
            { passive: false }
          );
        }
      }

      function onResizeImmediate() {
        const wrap = getActiveWrap();
        if (!isMobile()) {
          if (wrap) {
            wrap.classList.remove(fixedClass);
            wrap.classList.remove(hidingClass);
            setSentinelSpacer(false);
          }
          disconnectObservers();
          // 切到桌面也确保 AI 按钮释放（disconnectObservers 内已调，保险再调一次）
          setAiHidden(false);
        } else {
          initMobFixBtn();
        }
      }

      // resize debounce: 拖窗口/旋转屏幕时合并到一次重建
      function onResize() {
        if (resizeTimer) { clearTimeout(resizeTimer); }
        resizeTimer = setTimeout(function () {
          resizeTimer = null;
          onResizeImmediate();
        }, 150);
      }

      function scheduleInit() {
        scheduleFrame(initMobFixBtn);
      }
      // 浮动 CTA 只启动一次；异步内容由上面的局部 Observer 接管，不再在 load/延迟定时器中重复初始化。
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
      else scheduleInit();
      window.addEventListener('resize', onResize);
})();

(function () {
      const sectionId = 'landing-content-' + WB_LANDING_SECTION_ID;

      function resolveProductTarget() {
        return document.querySelector('#featureproduct, .featured-product');
      }

      function onHeroBtnClick(e) {
        const target = resolveProductTarget();
        if (!target) return; // 找不到商详则保持按钮默认行为
        e.preventDefault();
        const top =
          target.getBoundingClientRect().top +
          (window.pageYOffset || document.documentElement.scrollTop);
        window.scrollTo({ top: top, behavior: 'smooth' });
      }

      function bindHeroBtns() {
        const section = document.getElementById(sectionId);
        if (!section) return;
        const btns = section.querySelectorAll(
          '.red-flags__hero-intro-button, .red-flags__hero-intro-guarantee-link'
        );
        btns.forEach(function (btn) {
          if (btn.dataset.landingScrollBound === '1') return;
          btn.dataset.landingScrollBound = '1';
          btn.addEventListener('click', onHeroBtnClick, { passive: false });
        });
      }

      // data 标记保证按钮幂等绑定，因此不需要 600ms 后再次扫描。
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindHeroBtns, { once: true });
      else bindHeroBtns();
    })();

(function () {
      const sectionEl = document.getElementById('landing-content-' + WB_LANDING_SECTION_ID);
      if (!sectionEl) return;
  
      function initSlider(root) {
        if (!root || root.dataset.daadReviewSliderInit === '1') return;
        const track = root.querySelector('[data-daad-review-slider-track]');
        if (!track) return;
        const slides = track.querySelectorAll('.daad-review-slider__slide');
        if (!slides.length) return;
        const prevBtn = root.querySelector('.daad-review-slider__btn--prev');
        const nextBtn = root.querySelector('.daad-review-slider__btn--next');
        const dotsWrap = root.querySelector('[data-daad-review-slider-dots]');
        const mqMobile = window.matchMedia('(max-width: 768px)');
  
        let index = 0;
        // mobile: 单张 100% 全幅，desktop: 一屏 3 张
        let perView = mqMobile.matches ? 1 : 3;
        let slidePercent = mqMobile.matches ? 100 : (100 / 3);
        let centered = false;
        // 单张切换：可停留的起始位置数 = 总张数 - perView + 1
        let maxIndex = Math.max(0, slides.length - perView);
        let pages = maxIndex + 1;
  
        function buildDots() {
          if (!dotsWrap) return;
          dotsWrap.innerHTML = '';
          for (let i = 0; i < pages; i++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'daad-review-slider__dot' + (i === index ? ' is-active' : '');
            dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            (function (idx) {
              dot.addEventListener('click', function () { go(idx); });
            })(i);
            dotsWrap.appendChild(dot);
          }
        }
  
        function update() {
          let offset = -index * slidePercent;
          if (centered) {
            // 居中模式：让当前 slide 处于视口正中
            offset += (100 - slidePercent) / 2;
          }
          track.style.transform = 'translateX(' + offset + '%)';
          if (!dotsWrap) return;
          const dots = dotsWrap.querySelectorAll('.daad-review-slider__dot');
          for (let i = 0; i < dots.length; i++) {
            if (i === index) dots[i].classList.add('is-active');
            else dots[i].classList.remove('is-active');
          }
        }
  
        function go(i) {
          if (pages <= 1) { index = 0; update(); return; }
          if (i < 0) i = maxIndex;
          if (i > maxIndex) i = 0;
          index = i;
          update();
        }
  
        if (prevBtn) prevBtn.addEventListener('click', function () { go(index - 1); });
        if (nextBtn) nextBtn.addEventListener('click', function () { go(index + 1); });
  
        let startX = 0;
        let deltaX = 0;
        let dragging = false;
        track.addEventListener('touchstart', function (e) {
          if (!e.touches || !e.touches.length) return;
          dragging = true;
          startX = e.touches[0].clientX;
          deltaX = 0;
        }, { passive: true });
        track.addEventListener('touchmove', function (e) {
          if (!dragging || !e.touches || !e.touches.length) return;
          deltaX = e.touches[0].clientX - startX;
        }, { passive: true });
        track.addEventListener('touchend', function () {
          if (!dragging) return;
          dragging = false;
          if (Math.abs(deltaX) > 40) {
            if (deltaX < 0) go(index + 1);
            else go(index - 1);
          }
        });
  
        function handleResize() {
          const newPerView = mqMobile.matches ? 1 : 3;
          if (newPerView === perView) return;
          perView = newPerView;
          slidePercent = mqMobile.matches ? 100 : (100 / 3);
          centered = false;
          maxIndex = Math.max(0, slides.length - perView);
          pages = maxIndex + 1;
          if (index > maxIndex) index = maxIndex;
          buildDots();
          update();
        }
        if (mqMobile.addEventListener) mqMobile.addEventListener('change', handleResize);
        else if (mqMobile.addListener) mqMobile.addListener(handleResize);
  
        buildDots();
        update();
        root.dataset.daadReviewSliderInit = '1';
      }
  
      function initAll() {
        const sliders = sectionEl.querySelectorAll('[data-daad-review-slider]');
        sliders.forEach(initSlider);
      }

      let sliderStarted = false;
      let sliderObserver = null;

      function startSlider() {
        if (sliderStarted) return;
        sliderStarted = true;
        initAll();

        // 仅观察轮播自身的异步内容，避免 section 内其它 App/图片 DOM 变化触发重初始化。
        if ('MutationObserver' in window) {
          sliderObserver = new MutationObserver(initAll);
          const sliders = sectionEl.querySelectorAll('[data-daad-review-slider]');
          sliders.forEach(function (sliderRoot) {
            sliderObserver.observe(sliderRoot, { childList: true, subtree: true });
          });
        }
      }

      function startSliderWhenNearViewport() {
        const slider = sectionEl.querySelector('[data-daad-review-slider]');
        if (!slider || !('IntersectionObserver' in window)) {
          startSlider();
          return;
        }

        // 评论轮播属于下方内容；接近视口后再创建事件和圆点，旧浏览器仍立即可用。
        const visibilityObserver = new IntersectionObserver(function (entries) {
          if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
          visibilityObserver.disconnect();
          startSlider();
        }, { rootMargin: '300px 0px', threshold: 0.01 });
        visibilityObserver.observe(slider);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startSliderWhenNearViewport, { once: true });
      } else {
        startSliderWhenNearViewport();
      }
    })();
})();
